const toArray = (value) => (Array.isArray(value) ? value : []);

function childrenOf(node) {
  const children = toArray(node?.children);
  return children.length ? children : toArray(node?.folders);
}

export function extractNumericId(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const match = String(value).match(/(\d+)$/);
  return match ? Number(match[1]) : null;
}

/** Collects the files of every node in the given (sub)trees. */
export function collectTreeFiles(nodes) {
  const files = [];
  toArray(nodes).forEach((node) => {
    files.push(...toArray(node?.files));
    files.push(...collectTreeFiles(childrenOf(node)));
  });
  return files;
}

/** Flattens tree nodes into the folder shape ExperimentFiles expects. */
export function flattenTreeFolders(nodes, parentId = null, extras = {}) {
  const flattened = [];
  toArray(nodes).forEach((node, index) => {
    const stableId = node?.id != null ? node.id : `virtual:${node?.path || `${parentId}:${index}`}`;
    const backendId = extractNumericId(node?.id)
      ?? (typeof node?.backend_id === 'number' ? node.backend_id : null);
    const displayLabel = node?.structure_type || node?.display_entity_label || node?.type_name
      || node?.type_code || node?.entity_type || null;

    flattened.push({
      id: stableId,
      backend_id: backendId,
      type: node?.type || null,
      name: node?.name || '',
      path: node?.path || '',
      internal_path: node?.path || '',
      parent_path: node?.parent_path ?? null,
      parent_id: parentId,
      virtual: Boolean(node?.virtual),
      entity_type: node?.entity_type || null,
      entity_id: node?.entity_id ?? null,
      structure_type: node?.structure_type || null,
      structure_type_code: node?.structure_type_code || null,
      display_entity_label: displayLabel,
      order: Number.isFinite(Number(node?.order)) ? Number(node.order) : null,
      ...extras,
    });

    flattened.push(...flattenTreeFolders(childrenOf(node), stableId, extras));
  });
  return flattened;
}

/**
 * Parses a GET /files/tree response into the props ExperimentFiles needs.
 * Root-level files live on `tree.files`; the old `data.files` / `data.folders` shape is still accepted.
 */
export function parseFilesTree(data, { folderExtras = {} } = {}) {
  const tree = data?.tree ?? null;
  const nodes = tree ? childrenOf(tree) : toArray(data?.folders);

  const filesById = new Map();
  [...toArray(data?.files), ...toArray(tree?.files), ...collectTreeFiles(nodes)].forEach((file) => {
    if (file?.id != null) filesById.set(file.id, file);
  });

  return {
    files: Array.from(filesById.values()),
    folders: flattenTreeFolders(nodes, null, folderExtras),
    basePath: data?.base_path || '',
  };
}

const GROUPABLE_TYPES = new Set(['structure_element', 'experiment']);

function pluralize(label) {
  const text = String(label || '').trim();
  if (!text) return '';
  if (/[^aeiou]y$/i.test(text)) return `${text.slice(0, -1)}ies`;
  if (/s$/i.test(text)) return text;
  return `${text}s`;
}

/**
 * Moves virtual child entities (structure elements, experiments) of each parent into one
 * synthetic group folder per type, e.g. "Work Packages". Input folders are not mutated.
 * The `/__structure_group__/` path marker is what ExperimentFiles uses to treat a folder as a read-only group.
 */
export function groupVirtualChildren(folders) {
  const list = toArray(folders).filter(Boolean);
  const pathById = new Map(list.map((folder) => [folder.id, folder.path]));
  const groups = new Map();

  const rewritten = list.map((folder) => {
    const type = String(folder.type || '').toLowerCase();
    if (!folder.virtual || !GROUPABLE_TYPES.has(type)) return folder;

    const isExperiment = type === 'experiment';
    const label = isExperiment ? 'Experiment' : (folder.structure_type || 'Element');
    const code = isExperiment
      ? 'experiment'
      : (folder.structure_type_code || label.toLowerCase().replace(/\s+/g, '_'));
    const groupId = `group:${folder.parent_id ?? 'root'}:${code}`;

    if (!groups.has(groupId)) {
      const parentPath = pathById.get(folder.parent_id) || '';
      const path = `${parentPath}/__structure_group__/${code}`;
      groups.set(groupId, {
        id: groupId,
        backend_id: null,
        type: 'virtual_group',
        name: pluralize(label),
        path,
        internal_path: path,
        parent_path: parentPath || null,
        parent_id: folder.parent_id ?? null,
        virtual: true,
        entity_type: null,
        entity_id: null,
        structure_type: null,
        structure_type_code: null,
        display_entity_label: null,
        order: null,
      });
    }

    return { ...folder, parent_id: groupId };
  });

  return [...rewritten, ...groups.values()];
}
